import { QuotaNotice } from "@/components/billing/quota-notice";
import { AccessBadge } from "@/components/app/access-badge";
import { useMemo, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePremium } from "@/components/billing/premium-lock";
import { useQuotaUpgrade } from "@/components/billing/use-quota-upgrade";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, ClipboardList, Flame, Plus, Search, Sparkles } from "lucide-react";
import {
  completeQuestionPractice,
  createQuestionSetFn,
  fetchQuestionChapters,
  removeQuestionChapter,
  rescheduleQuestionSession,
  saveQuestionTarget,
} from "@/lib/questions.functions";
import { formatPrettyDate, localDateString } from "@/lib/app-types";
import {
  QUESTION_SUBJECTS,
  chapterHealth,
  chapterProgress,
  plannedTarget,
  practiceStreak,
  subjectMeta,
  taskState,
  type QuestionChapter,
  type QuestionTask,
} from "@/lib/question-plan";
import { QuestionChapterCard } from "@/components/app/question-chapter-card";
import {
  QuestionSetSheet,
  type QuestionSetInput,
} from "@/components/app/question-set-sheet";
import { ProgressRing } from "@/components/app/progress-ring";
import { BottomSheet } from "@/components/app/bottom-sheet";
import { RippleButton } from "@/components/app/ripple-button";
import { friendlyError, notifySaved, trackSave } from "@/lib/save-feedback";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/questions")({
  head: () => ({
    ...seo({
      title: "Question Tracker — The Quantum Minds",
      description:
        "Automatic 180-question practice plans per completed chapter, with day 1, 3, 7, 15, 30 and 60 sessions for JEE & NEET.",
      path: "/questions",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Question Tracker", path: "/questions" },
      ]),
    ],
  }),
  component: QuestionsPage,
});

type Filter =
  | "all"
  | "Physics"
  | "Chemistry"
  | "Maths"
  | "Other"
  | "today"
  | "pending"
  | "completed"
  | "overdue"
  | "strong"
  | "weak";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "Physics", label: "Physics" },
  { key: "Chemistry", label: "Chemistry" },
  { key: "Maths", label: "Mathematics" },
  { key: "Other", label: "Others" },
  { key: "today", label: "Today's tasks" },
  { key: "pending", label: "Pending" },
  { key: "completed", label: "Completed" },
  { key: "overdue", label: "Overdue" },
  { key: "strong", label: "Strong" },
  { key: "weak", label: "Weak" },
];

function QuestionsPage() {
  const { requirePremium } = usePremium();
  const showQuotaUpgrade = useQuotaUpgrade();
  const queryClient = useQueryClient();
  const today = localDateString();
  const [adding, setAdding] = useState(false);
  const [logging, setLogging] = useState<
    { chapter: QuestionChapter; task: QuestionTask; target: number } | null
  >(null);
  const [attemptedInput, setAttemptedInput] = useState("0");
  const [correctInput, setCorrectInput] = useState("0");
  const [rescheduling, setRescheduling] = useState<QuestionTask | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState(today);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<QuestionChapter | null>(null);
  const [deleting, setDeleting] = useState<QuestionChapter | null>(null);
  const [targetInput, setTargetInput] = useState("180");
  const [error, setError] = useState<string | null>(null);
  const [justDone, setJustDone] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["question-chapters"],
    queryFn: () => fetchQuestionChapters(),
  });

  const chapters = useMemo(() => data ?? [], [data]);

  const complete = useMutation({
    mutationFn: (vars: {
      taskId: string;
      solved: number;
      attempted?: number;
      correct?: number;
      incorrect?: number;
    }) =>
      completeQuestionPractice({
        data: {
          taskId: vars.taskId,
          today,
          solved: vars.solved,
          attempted: vars.attempted,
          correct: vars.correct,
          incorrect: vars.incorrect,
        },
      }),
    retry: 2,
    onMutate: (vars) => {
      const stop = trackSave("complete-practice");
      setError(null);
      setJustDone(vars.taskId);
      window.setTimeout(() => setJustDone(null), 1600);
      const previous = queryClient.getQueryData<QuestionChapter[]>(["question-chapters"]);
      queryClient.setQueryData<QuestionChapter[]>(["question-chapters"], (old) =>
        (old ?? []).map((chapter) =>
          chapter.tasks.some((t) => t.id === vars.taskId)
            ? {
                ...chapter,
                solvedQuestions: chapter.solvedQuestions + vars.solved,
                tasks: chapter.tasks.map((t) =>
                  t.id === vars.taskId
                    ? {
                        ...t,
                        status: "completed" as const,
                        solvedQuestions: vars.solved,
                        attemptedQuestions: vars.attempted ?? vars.solved,
                        correctQuestions: vars.correct ?? 0,
                        incorrectQuestions:
                          vars.incorrect ?? Math.max(0, (vars.attempted ?? vars.solved) - (vars.correct ?? 0)),
                        completedAt: new Date().toISOString(),
                      }
                    : t,
                ),
              }
            : chapter,
        ),
      );
      notifySaved("Practice saved");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["question-chapters"], ctx.previous);
      showQuotaUpgrade(err);
      setError(friendlyError(err));
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["question-chapters"] }),
  });

  const saveTarget = useMutation({
    mutationFn: (vars: { chapterId: string; target: number }) =>
      saveQuestionTarget({ data: vars }),
    retry: 2,
    onMutate: (vars) => {
      const stop = trackSave("question-target");
      setEditing(null);
      const previous = queryClient.getQueryData<QuestionChapter[]>(["question-chapters"]);
      queryClient.setQueryData<QuestionChapter[]>(["question-chapters"], (old) =>
        (old ?? []).map((chapter) =>
          chapter.id === vars.chapterId
            ? { ...chapter, targetQuestions: vars.target }
            : chapter,
        ),
      );
      notifySaved("Target updated");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["question-chapters"], ctx.previous);
      showQuotaUpgrade(err);
      setError(friendlyError(err));
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["question-chapters"] }),
  });

  const deleteChapter = useMutation({
    mutationFn: (vars: { chapterId: string }) => removeQuestionChapter({ data: vars }),
    onMutate: (vars) => {
      const stop = trackSave("delete-question-chapter");
      setDeleting(null);
      setError(null);
      const previous = queryClient.getQueryData<QuestionChapter[]>(["question-chapters"]);
      queryClient.setQueryData<QuestionChapter[]>(["question-chapters"], (old) =>
        (old ?? []).filter((chapter) => chapter.id !== vars.chapterId),
      );
      notifySaved("Chapter deleted");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["question-chapters"], ctx.previous);
      setError(friendlyError(err));
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["question-chapters"] }),
  });

  const createSet = useMutation({
    mutationFn: (vars: QuestionSetInput) =>
      createQuestionSetFn({ data: { ...vars, today } }),
    onMutate: () => {
      setError(null);
      return { stop: trackSave("create-question-set") };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      showQuotaUpgrade(err);
      setError(friendlyError(err));
    },
    onSuccess: (_res, _vars, ctx) => {
      ctx?.stop("ok");
      setAdding(false);
      notifySaved("Question set created");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["question-chapters"] }),
  });

  const reschedule = useMutation({
    mutationFn: (vars: { taskId: string; date: string }) =>
      rescheduleQuestionSession({ data: vars }),
    onMutate: (vars) => {
      const stop = trackSave("reschedule-question-session");
      setRescheduling(null);
      setError(null);
      const previous = queryClient.getQueryData<QuestionChapter[]>(["question-chapters"]);
      queryClient.setQueryData<QuestionChapter[]>(["question-chapters"], (old) =>
        (old ?? []).map((chapter) => ({
          ...chapter,
          tasks: chapter.tasks.map((t) =>
            t.id === vars.taskId ? { ...t, dueDate: vars.date, rescheduled: true } : t,
          ),
        })),
      );
      notifySaved("Session moved");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["question-chapters"], ctx.previous);
      setError(friendlyError(err));
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["question-chapters"] }),
  });

  function openLog(chapter: QuestionChapter, task: QuestionTask, target: number) {
    if (!requirePremium()) return;
    setError(null);
    setAttemptedInput(`${target}`);
    setCorrectInput(`${target}`);
    setLogging({ chapter, task, target });
  }

  const totals = useMemo(() => {
    let solved = 0;
    let target = 0;
    let todayTarget = 0;
    for (const chapter of chapters) {
      const p = chapterProgress(chapter);
      solved += p.solved;
      target += chapter.targetQuestions;
      const todayTask = chapter.tasks.find((t) => taskState(t, today) === "today");
      if (todayTask) todayTarget += plannedTarget(chapter, todayTask, today);
    }
    return {
      solved,
      target,
      remaining: Math.max(0, target - solved),
      todayTarget,
      percent: target ? Math.round((solved / target) * 100) : 0,
      streak: practiceStreak(chapters, today),
    };
  }, [chapters, today]);

  const todayCards = useMemo(
    () =>
      chapters
        .map((chapter) => {
          const task = chapter.tasks.find((t) => taskState(t, today) === "today");
          return task ? { chapter, task } : null;
        })
        .filter((v): v is { chapter: QuestionChapter; task: QuestionTask } => v !== null),
    [chapters, today],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chapters.filter((chapter) => {
      if (q && !chapter.chapter.toLowerCase().includes(q)) return false;
      const states = chapter.tasks.map((t) => taskState(t, today));
      const health = chapterHealth(chapter, today);
      switch (filter) {
        case "all":
          return true;
        case "today":
          return states.includes("today");
        case "overdue":
          return states.includes("overdue");
        case "pending":
          return chapter.tasks.some((t) => t.status === "pending");
        case "completed":
          return chapter.tasks.every((t) => t.status === "completed");
        case "strong":
          return health.tone === "strong";
        case "weak":
          return health.tone === "weak";
        default:
          return chapter.subject === filter;
      }
    });
  }, [chapters, filter, search, today]);

  const bySubject = useMemo(
    () =>
      QUESTION_SUBJECTS.map((subject) => ({
        ...subject,
        items: visible.filter((c) => c.subject === subject.key),
      })).filter((group) => group.items.length > 0),
    [visible],
  );

  return (
    <div className="mx-auto min-h-screen w-full max-w-7xl space-y-6 px-4 pb-4 pt-8 sm:px-6 lg:px-8">
      <header>
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-[26px] font-extrabold leading-tight tracking-tight">
            Question Tracker <AccessBadge level="PRO + FREE" />
          </h1>
          <ShareButton className="mt-1" />
        </div>

        <p className="mt-1.5 text-sm text-muted-foreground">
          Track questions by chapter and source — auto plans or your own schedule.
        </p>
        <RippleButton className="mt-4 w-full" onClick={() => setAdding(true)}>
          <Plus className="size-4" /> New question set
        </RippleButton>
      </header>

      {chapters.length > 0 ? (
        <section className="glass mt-5 rounded-[26px] p-5">
          <div className="flex items-center gap-4">
            <ProgressRing progress={totals.percent} size={104} stroke={9}>
              <div className="text-center">
                <p className="text-xl font-extrabold tabular-nums">{totals.percent}%</p>
                <p className="text-[10px] text-muted-foreground">solved</p>
              </div>
            </ProgressRing>
            <div className="grid flex-1 grid-cols-2 gap-2">
              {[
                { label: "Solved", value: totals.solved },
                { label: "Remaining", value: totals.remaining },
                { label: "Today", value: totals.todayTarget },
                { label: "Chapters", value: chapters.length },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-hairline bg-surface p-2.5">
                  <p className="text-base font-extrabold tabular-nums">{stat.value}</p>
                  <p className="text-[10px] text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Flame className="size-3.5 text-warning" />
            {totals.streak} day practice streak
          </p>
        </section>
      ) : null}

      {todayCards.length > 0 ? (
        <section className="mt-5">
          <p className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-primary">
            <Sparkles className="size-3.5" /> Today&apos;s question practice
          </p>
           <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            {todayCards.map(({ chapter, task }) => {
              const target = plannedTarget(chapter, task, today);
              const done = justDone === task.id;
              return (
                <motion.div
                  key={task.id}
                  layout
                  className="rounded-[24px] border border-primary/20 bg-primary/8 p-5"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    {subjectMeta(chapter.subject).emoji} {subjectMeta(chapter.subject).label}
                  </p>
                  <h2 className="mt-1 text-lg font-extrabold tracking-tight">{chapter.chapter}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Today&apos;s target · <span className="font-semibold text-foreground">{target} questions</span>
                  </p>
                  <RippleButton
                    size="md"
                    className="mt-4 w-full"
                    disabled={complete.isPending}
                    onClick={() => openLog(chapter, task, target)}
                  >
                    {done ? "✅ Completed" : "Start Practice"}
                  </RippleButton>
                </motion.div>
              );
            })}
          </div>
        </section>
      ) : null}

      {chapters.length > 0 ? (
        <>
          <div className="glass mt-5 flex items-center gap-2 rounded-full px-4 py-2.5">
            <Search className="size-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a chapter…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {FILTERS.map((item) => {
              const active = filter === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => setFilter(item.key)}
                  className={cn(
                    "relative shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors",
                    active ? "text-primary-foreground" : "bg-secondary/50 text-muted-foreground",
                  )}
                >
                  {active ? (
                    <motion.span
                      layoutId="question-filter"
                      transition={{ type: "spring", stiffness: 320, damping: 30 }}
                      className="absolute inset-0 rounded-full [background-image:var(--gradient-brand)]"
                    />
                  ) : null}
                  <span className="relative">{item.label}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      <QuotaNotice message={error} className="mt-4" />

      {isPending ? (
        <div className="mt-6 card-grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="glass animate-shimmer h-32 overflow-hidden rounded-[24px]" />
          ))}
        </div>
      ) : chapters.length === 0 ? (
        <div className="glass mt-8 rounded-[26px] p-8 text-center">
          <div className="mx-auto grid size-16 place-items-center rounded-3xl [background-image:var(--gradient-brand)]">
            <ClipboardList className="size-7 text-primary-foreground" />
          </div>
          <p className="mt-4 font-semibold">No chapters yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create your first question set to start tracking.
          </p>
          <RippleButton className="mt-5" onClick={() => setAdding(true)}>
            New question set
          </RippleButton>
        </div>
      ) : bySubject.length === 0 ? (
        <div className="glass mt-6 rounded-[26px] p-8 text-center">
          <p className="font-semibold">No matches</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try another filter or search term.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {bySubject.map((group) => {
            const isClosed = collapsed[group.key] === true;
            return (
              <section
                key={group.key}
                className="rounded-[26px] border border-hairline bg-surface p-3"
              >
                <button
                  onClick={() => setCollapsed((prev) => ({ ...prev, [group.key]: !isClosed }))}
                  className="flex w-full items-center gap-3 px-2 py-1.5"
                >
                  <span className="text-base">{group.emoji}</span>
                  <span className="flex-1 text-left text-sm font-bold tracking-tight">
                    {group.label}
                  </span>
                  <span className="rounded-full bg-secondary/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                    {group.items.length}
                  </span>
                  <ChevronDown
                    className={cn(
                      "size-4 text-muted-foreground transition-transform",
                      isClosed && "-rotate-90",
                    )}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {!isClosed ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-3 overflow-hidden pt-3"
                    >
                      {group.items.map((chapter, i) => (
                        <QuestionChapterCard
                          key={chapter.id}
                          chapter={chapter}
                          today={today}
                          index={i}
                          busy={complete.isPending}
                          onComplete={(task, target) => openLog(chapter, task, target)}
                          onReschedule={(task) => {
                            setRescheduleDate(task.dueDate);
                            setRescheduling(task);
                          }}
                          onEditTarget={(c) => {
                            setEditing(c);
                            setTargetInput(`${c.targetQuestions}`);
                          }}
                          onDelete={(c) => setDeleting(c)}
                        />
                      ))}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </section>
            );
          })}
        </div>
      )}

      <BottomSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Question target"
        subtitle={editing?.chapter}
      >
        <div className="space-y-4">
          <input
            type="number"
            inputMode="numeric"
            min={10}
            max={2000}
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            className="w-full rounded-2xl border border-hairline bg-surface px-4 py-3 text-base font-semibold outline-none focus:border-primary/50"
          />
          <p className="text-xs text-muted-foreground">
            Next practice:{" "}
            {editing?.tasks.find((t) => t.status === "pending")
              ? formatPrettyDate(editing.tasks.find((t) => t.status === "pending")!.dueDate)
              : "plan finished"}
          </p>
          <RippleButton
            className="w-full"
            disabled={saveTarget.isPending}
            onClick={() =>
              editing && requirePremium()
                ? saveTarget.mutate({
                    chapterId: editing.id,
                    target: Math.max(10, Math.min(2000, Number(targetInput) || 180)),
                  })
                : undefined
            }
          >
            Save target
          </RippleButton>
        </div>
      </BottomSheet>

      <BottomSheet
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete chapter"
        subtitle={deleting?.chapter}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This permanently removes this chapter and all of its practice sessions and solved
            counts. Other chapters are not affected. This cannot be undone.
          </p>
          <div className="flex gap-2">
            <RippleButton variant="glass" className="flex-1" onClick={() => setDeleting(null)}>
              Cancel
            </RippleButton>
            <RippleButton
              className="flex-1 text-destructive"
              variant="glass"
              disabled={deleteChapter.isPending}
              onClick={() =>
                deleting ? deleteChapter.mutate({ chapterId: deleting.id }) : undefined
              }
            >
              Delete
            </RippleButton>
          </div>
        </div>
      </BottomSheet>

      <QuestionSetSheet
        open={adding}
        busy={createSet.isPending}
        onClose={() => setAdding(false)}
        onSubmit={(input) => {
          if (!requirePremium()) return;
          createSet.mutate(input);
        }}
      />

      <BottomSheet
        open={logging !== null}
        onClose={() => setLogging(null)}
        title="Log this practice"
        subtitle={logging?.chapter.chapter}
      >
        <div className="space-y-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Questions attempted
            </p>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={500}
              value={attemptedInput}
              onChange={(e) => setAttemptedInput(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 text-base font-semibold outline-none focus:border-primary/50"
            />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Correct answers
            </p>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={500}
              value={correctInput}
              onChange={(e) => setCorrectInput(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 text-base font-semibold outline-none focus:border-primary/50"
            />
          </div>
          <RippleButton
            className="w-full"
            disabled={complete.isPending}
            onClick={() => {
              if (!logging) return;
              const attempted = Math.max(0, Math.min(Number(attemptedInput) || 0, 500));
              const correct = Math.max(0, Math.min(Number(correctInput) || 0, attempted));
              const item = logging;
              setLogging(null);
              complete.mutate({
                taskId: item.task.id,
                solved: attempted || item.target,
                attempted,
                correct,
                incorrect: Math.max(0, attempted - correct),
              });
            }}
          >
            Save practice
          </RippleButton>
        </div>
      </BottomSheet>

      <BottomSheet
        open={rescheduling !== null}
        onClose={() => setRescheduling(null)}
        title="Reschedule session"
        subtitle={
          rescheduling ? `Currently ${formatPrettyDate(rescheduling.dueDate)}` : undefined
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Pick a new date for this session. Your other sessions stay exactly where you put
            them.
          </p>
          <input
            type="date"
            value={rescheduleDate}
            onChange={(e) => setRescheduleDate(e.target.value)}
            className="w-full appearance-none rounded-2xl border border-hairline bg-surface px-4 py-3 text-base font-semibold outline-none focus:border-primary/50"
          />
          <RippleButton
            className="w-full"
            disabled={reschedule.isPending}
            onClick={() =>
              rescheduling
                ? reschedule.mutate({ taskId: rescheduling.id, date: rescheduleDate })
                : undefined
            }
          >
            Move session
          </RippleButton>
        </div>
      </BottomSheet>
    </div>
  );
}